"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Upload, X } from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { AeraImage } from "@/app/components/ui/AeraImage";
import {
    WORKSPACE_AVATAR_COLORS,
    WORKSPACE_AVATAR_ICONS,
    draftToPreviewSelection,
    type WorkspaceAvatarIconId,
    type WorkspaceAvatarSelection,
} from "@/lib/workspaceAvatar";

type WorkspaceAvatarPickerModalProps = {
    isOpen: boolean;
    onClose: () => void;
    value: WorkspaceAvatarSelection;
    onChange: (value: WorkspaceAvatarSelection) => void;
    onDraftPreviewChange?: (value: WorkspaceAvatarSelection) => void;
    anchorRef?: React.RefObject<HTMLElement | null>;
};

type PickerDraft = {
    tab: "icon" | "image";
    iconKey: WorkspaceAvatarIconId;
    color: string;
    imagePreview: string | null;
    imageFile: File | null;
};

type PanelLayout = "popover" | "sheet";

type PopoverPosition = {
    top: number;
    left: number;
    maxHeight: number;
    placement: "above" | "below";
};

const VALID_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const PANEL_WIDTH = 320;
const PANEL_ESTIMATED_HEIGHT = 400;
/** ใช้ bottom sheet เฉพาะจอแคบมาก — desktop/tablet ใช้ popover ติดปุ่ม */
const SHEET_MAX_WIDTH_PX = 480;

function draftFromValue(value: WorkspaceAvatarSelection): PickerDraft {
    if (value.mode === "image") {
        return {
            tab: "image",
            iconKey: "folder",
            color: WORKSPACE_AVATAR_COLORS[0].value,
            imagePreview: value.preview,
            imageFile: value.file,
        };
    }
    return {
        tab: "icon",
        iconKey: value.iconKey,
        color: value.color,
        imagePreview: null,
        imageFile: null,
    };
}

function shouldUseSheetLayout(): boolean {
    if (typeof window === "undefined") return false;
    return window.innerWidth < SHEET_MAX_WIDTH_PX;
}

export function WorkspaceAvatarPickerModal({
    isOpen,
    onClose,
    value,
    onChange,
    onDraftPreviewChange,
    anchorRef,
}: WorkspaceAvatarPickerModalProps) {
    const fileInputId = useId().replace(/:/g, "");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);
    const [draft, setDraft] = useState<PickerDraft>(() => draftFromValue(value));
    const [layout, setLayout] = useState<PanelLayout>("popover");
    const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null);
    const [imageError, setImageError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setDraft(draftFromValue(value));
        setImageError(null);
    }, [isOpen, value]);

    const previewSelection = useMemo(
        () => draftToPreviewSelection(draft),
        [draft],
    );

    useEffect(() => {
        if (!isOpen) return;
        onDraftPreviewChange?.(previewSelection);
    }, [isOpen, previewSelection, onDraftPreviewChange]);

    useEffect(() => {
        if (!isOpen) {
            setLayout("popover");
            setPopoverPosition(null);
            return;
        }

        const updateLayout = () => {
            if (shouldUseSheetLayout() || !anchorRef?.current) {
                setLayout("sheet");
                setPopoverPosition(null);
                return;
            }

            const el = anchorRef.current;
            if (!el) return;

            const r = el.getBoundingClientRect();
            const margin = 12;
            const panelHeight = Math.min(
                PANEL_ESTIMATED_HEIGHT,
                window.innerHeight - margin * 2,
            );
            const spaceBelow = window.innerHeight - r.bottom - margin;
            const spaceAbove = r.top - margin;

            const placeAbove =
                spaceBelow < panelHeight && spaceAbove >= spaceBelow;
            const top = placeAbove
                ? Math.max(margin, r.top - panelHeight - 8)
                : r.bottom + 8;

            const maxHeight = placeAbove
                ? r.top - margin - 8
                : window.innerHeight - top - margin;

            let left = r.left;
            left = Math.min(
                Math.max(margin, left),
                window.innerWidth - PANEL_WIDTH - margin,
            );

            setLayout("popover");
            setPopoverPosition({
                top,
                left,
                maxHeight: Math.max(200, maxHeight),
                placement: placeAbove ? "above" : "below",
            });
        };

        updateLayout();
        window.addEventListener("resize", updateLayout);
        window.addEventListener("scroll", updateLayout, true);
        return () => {
            window.removeEventListener("resize", updateLayout);
            window.removeEventListener("scroll", updateLayout, true);
        };
    }, [isOpen, anchorRef]);

    useEffect(() => {
        if (!isOpen) return;
        const prevOverflow = document.body.style.overflow;
        if (layout === "sheet") {
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen, layout]);

    const scheduleClose = () => {
        requestAnimationFrame(() => onCloseRef.current());
    };

    useEffect(() => {
        if (!isOpen || layout === "sheet") return;
        const onPointerDown = (e: PointerEvent) => {
            const t = e.target as Node;
            if (panelRef.current?.contains(t)) return;
            if (anchorRef?.current?.contains(t)) return;
            scheduleClose();
        };
        document.addEventListener("pointerdown", onPointerDown, { capture: true });
        return () => document.removeEventListener("pointerdown", onPointerDown, { capture: true });
    }, [isOpen, layout, anchorRef]);

    const handleDismiss = () => {
        scheduleClose();
    };

    const handleDone = () => {
        if (draft.tab === "icon") {
            onChange({ mode: "icon", iconKey: draft.iconKey, color: draft.color });
            scheduleClose();
            return;
        }

        if (!draft.imagePreview?.trim()) {
            setImageError("กรุณาอัปโหลดรูปภาพก่อนกดบันทึก");
            return;
        }

        onChange({
            mode: "image",
            preview: draft.imagePreview,
            file: draft.imageFile,
        });
        scheduleClose();
    };

    const handleImageFile = (file: File) => {
        setImageError(null);
        if (!VALID_IMAGE_TYPES.includes(file.type)) {
            setImageError("กรุณาเลือกไฟล์รูปภาพ (JPEG, PNG, GIF, หรือ WebP)");
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setImageError("ขนาดรูปต้องไม่เกิน 5MB");
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            setDraft((prev) => ({
                ...prev,
                tab: "image",
                imagePreview: reader.result as string,
                imageFile: file,
            }));
        };
        reader.readAsDataURL(file);
    };

    const canConfirm =
        draft.tab === "icon" || Boolean(draft.imagePreview?.trim());

    if (!isOpen || typeof document === "undefined") return null;

    const isSheet = layout === "sheet";
    const isCenteredPopover = !isSheet && !popoverPosition;

    const popoverStyle: React.CSSProperties | undefined = !isSheet && popoverPosition
        ? {
              top: popoverPosition.top,
              left: popoverPosition.left,
              width: PANEL_WIDTH,
              maxHeight: popoverPosition.maxHeight,
              transformOrigin: popoverPosition.placement === "above" ? "bottom left" : "top left",
          }
        : undefined;

    return createPortal(
        <>
            <div
                className={[
                    "fixed inset-0 z-109",
                    isSheet
                        ? "bg-aera-black/40 cursor-pointer workspace-picker-backdrop-in"
                        : "bg-transparent pointer-events-none",
                ].join(" ")}
                aria-hidden={!isSheet}
                onClick={isSheet ? handleDismiss : undefined}
            />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                className={[
                    "fixed z-110 flex flex-col bg-aera-white shadow-xl overflow-hidden",
                    isSheet
                        ? "inset-x-0 bottom-0 rounded-t-2xl border-t border-aera-lightgray max-h-[min(88dvh,520px)] workspace-picker-sheet-in"
                        : [
                              "rounded-2xl border border-aera-lightgray",
                              isCenteredPopover
                                  ? "workspace-picker-popover-centered-in"
                                  : popoverPosition?.placement === "above"
                                    ? "workspace-picker-popover-in-above"
                                    : "workspace-picker-popover-in",
                          ].join(" "),
                ].join(" ")}
                style={
                    isSheet
                        ? undefined
                        : popoverStyle ?? {
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              width: PANEL_WIDTH,
                              maxHeight: "min(88vh, 520px)",
                          }
                }
            >
                {isSheet ? (
                    <div className="shrink-0 flex justify-center pt-2 pb-1">
                        <div className="w-10 h-1 rounded-full bg-aera-lightgray" aria-hidden />
                    </div>
                ) : null}

                <div className="flex border-b border-aera-lightgray shrink-0">
                    <AeraButton
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => setDraft((d) => ({ ...d, tab: "icon" }))}
                        className={[
                            "flex-1! py-2.5! text-sm! font-medium! transition-colors! rounded-none!",
                            draft.tab === "icon"
                                ? "text-aera-charcoal! border-b-2! border-aera-charcoal!"
                                : "text-aera-gray! hover:text-aera-charcoal!",
                        ].join(" ")}
                    >
                        ไอคอน
                    </AeraButton>
                    <AeraButton
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => setDraft((d) => ({ ...d, tab: "image" }))}
                        className={[
                            "flex-1! py-2.5! text-sm! font-medium! transition-colors! rounded-none!",
                            draft.tab === "image"
                                ? "text-aera-charcoal! border-b-2! border-aera-charcoal!"
                                : "text-aera-gray! hover:text-aera-charcoal!",
                        ].join(" ")}
                    >
                        รูปภาพ
                    </AeraButton>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                    {draft.tab === "icon" ? (
                        <div className="p-3">
                            <div className="flex flex-wrap gap-2 pb-3 mb-3 border-b border-aera-lightgray">
                                {WORKSPACE_AVATAR_COLORS.map((c) => {
                                    const selected = draft.color === c.value;
                                    return (
                                        <AeraButton
                                            key={c.id}
                                            variant="ghost"
                                            size="xs"
                                            type="button"
                                            onClick={() =>
                                                setDraft((d) => ({ ...d, color: c.value }))
                                            }
                                            className={[
                                                "w-7! h-7! min-h-0! p-0! rounded-full! transition-transform! hover:scale-105!",
                                                selected
                                                    ? "ring-2! ring-offset-2! ring-aera-charcoal!"
                                                    : "",
                                            ].join(" ")}
                                            style={{ backgroundColor: c.value }}
                                            aria-label={c.id}
                                        />
                                    );
                                })}
                            </div>
                            <div className="grid grid-cols-6 gap-1">
                                {WORKSPACE_AVATAR_ICONS.map(({ id, Icon }) => {
                                    const selected = draft.iconKey === id;
                                    return (
                                        <AeraButton
                                            key={id}
                                            variant="ghost"
                                            size="xs"
                                            type="button"
                                            onClick={() =>
                                                setDraft((d) => ({ ...d, iconKey: id }))
                                            }
                                            className={[
                                                "aspect-square! rounded-lg! flex! items-center! justify-center! transition-colors! p-0! min-h-0!",
                                                selected ? "bg-aera-lightgray!" : "hover:bg-aera-white!",
                                            ].join(" ")}
                                            style={
                                                selected
                                                    ? { backgroundColor: `${draft.color}22` }
                                                    : undefined
                                            }
                                        >
                                            <Icon
                                                size={20}
                                                style={{
                                                    color: selected ? draft.color : undefined,
                                                }}
                                                className={selected ? "" : "text-aera-charcoal"}
                                                strokeWidth={1.75}
                                            />
                                        </AeraButton>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 space-y-3">
                            <div className="mx-auto w-full max-w-[200px]">
                                {draft.imagePreview ? (
                                    <div className="relative w-full">
                                        <div className="relative aspect-square w-full overflow-hidden rounded-xl border border-aera-lightgray/80 bg-aera-white">
                                            <AeraImage
                                                src={draft.imagePreview}
                                                alt=""
                                                fill
                                                sizes="200px"
                                                className="h-full w-full object-cover"
                                            />
                                        </div>
                                        <AeraButton
                                            variant="ghost"
                                            size="xs"
                                            type="button"
                                            onClick={() => {
                                                setImageError(null);
                                                setDraft((prev) => ({
                                                    ...prev,
                                                    imagePreview: null,
                                                    imageFile: null,
                                                }));
                                            }}
                                            className="absolute! -top-3! -right-3! flex! h-8! w-8! min-h-0! p-0! items-center! justify-center! rounded-full! border! border-aera-lightgray! bg-aera-white! text-aera-charcoal! shadow-md! transition-colors! hover:border-aera-red/30! hover:bg-aera-redlight! hover:text-aera-red!"
                                            aria-label="ลบรูปภาพ"
                                        >
                                            <X className="h-4 w-4" />
                                        </AeraButton>
                                    </div>
                                ) : (
                                    <label
                                        htmlFor={fileInputId}
                                        className="flex aspect-square w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-aera-lightgray bg-aera-white px-4 text-sm font-medium text-aera-charcoal transition-all hover:border-aera-amber hover:bg-aera-lightgray hover:text-aera-amber"
                                    >
                                        <Upload className="h-5 w-5" />
                                        อัปโหลดรูปภาพ
                                    </label>
                                )}
                                <input
                                    ref={fileInputRef}
                                    id={fileInputId}
                                    type="file"
                                    className="hidden"
                                    accept={VALID_IMAGE_TYPES.join(",")}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleImageFile(file);
                                        e.target.value = "";
                                    }}
                                />
                            </div>
                            {imageError ? (
                                <p className="text-xs text-aera-red text-center">{imageError}</p>
                            ) : null}
                            <p className="text-xs text-aera-gray text-center">
                                JPEG, PNG, GIF, WebP (สูงสุด 5MB)
                            </p>
                        </div>
                    )}
                </div>

                <div className="shrink-0 flex gap-2 p-3 border-t border-aera-lightgray/80 bg-aera-offwhite pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                    <AeraButton
                        variant="outline"
                        size="sm"
                        fontWeight="medium"
                        onClick={handleDismiss}
                        className="flex-1"
                    >
                        ปิด
                    </AeraButton>
                    <AeraButton
                        variant="primary"
                        size="sm"
                        fontWeight="medium"
                        onClick={handleDone}
                        disabled={!canConfirm}
                        className="flex-1"
                    >
                        บันทึก
                    </AeraButton>
                </div>
            </div>
        </>,
        document.body,
    );
}
